import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-75dbb359"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "floor-measurement-row" }
const _hoisted_2 = { class: "no-label-input" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "empty-div" }, null, -1))
const _hoisted_4 = { class: "floor-measurement-row" }
const _hoisted_5 = { class: "no-label-input" }
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "empty-div" }, null, -1))

import SystemCodeSingleSelect from "@/components/ui/system-codes/system-code-single-select.vue";
import { CodeType } from "acquaint-api/constants/CodeType";
import {
  convertMetresToFeetAndInches,
  convertFeetToMetres,
  convertMetresToFeetAndInchesString,
  convertMetresAndCentimetresToMetres,
  convertMetresToMetresAndCentimetres,
} from "@/utils/ConvertDistanceMeasurementType";
import AcquaintInfoBox from "@/components/ui/inputs/acquaint-info-box.vue";
import NumberBoxField from "@/components/ui/inputs/number-box-field.vue";
import { computed, ref, watch, onMounted } from "vue";
import { MetricImperial } from "@/enums/MetricImperial";
import { FieldSize } from "@/enums/system/FieldSize";


export default /*#__PURE__*/_defineComponent({
  __name: 'edit-room-measurements',
  props: {
  /**
   * The string value used for the first set of fields.
   * @type {string}
   * @default ""
   */
  firstLabel: {
    type: String,
    default: "",
  },
  /**
   * The string value used for the second set of fields.
   * @type {string}
   * @default ""
   */
  secondLabel: {
    type: String,
    default: "",
  },
  /**
   * The string value is used for determining what measurement type the values will be displayed in.
   * @type {string}
   * @default MetricImperial.Imperial
   */
  measurementType: {
    type: String,
    default: MetricImperial.Imperial,
  },
  /**
   * The number value is used for determining the total width.
   * @type {number}
   * @default 0
   */
  width: {
    type: Number,
    default: 0,
  },
  /**
   * The number value is used for determining the total length.
   * @type {number}
   * @default 0
   */
  length: {
    type: Number,
    default: 0,
  },
  /**
   * The number value is used for reset the field values.
   * @type {number}
   * @default 0
   */
  formatData: {
    type: Number,
    default: 0,
  },
  /**
   * The number value is used for determining the selected value of the length suffix dropdown.
   * @type {number}
   * @default 0
   */
  lengthSuffix: {
    type: Number,
    default: 0,
  },
  /**
   * The number value is used for determining the selected value of the width suffix dropdown.
   * @type {number}
   * @default 0
   */
  widthSuffix: {
    type: Number,
    default: 0,
  },
  /**
   * The number value is used for determining whetehr or not the suffix fields are actually displayed.
   * @type {boolean}
   * @default true
   */
  displaySuffix: {
    type: Boolean,
    default: true,
  },
},
  emits: [
  /**
   * Triggered when the `length` changes.
   * @event update:length
   * @property {number} value - The updated state of the length.
   */
  "update:length",

  /**
   * Triggered when the `width` changes.
   * @event update:width
   * @property {number} value - The updated state of the width.
   */
  "update:width",

  /**
   * Triggered when the `lengthSuffix` changes.
   * @event update:lengthSuffix
   * @property {number} value - The updated state of the lengthSuffix.
   */
  "update:lengthSuffix",

  /**
   * Triggered when the `widthSuffix` changes.
   * @event update:widthSuffix
   * @property {number} value - The updated state of the widthSuffix.
   */
  "update:widthSuffix",

  /**
   * Triggered when the floorArea is updated.
   * @event update:floorArea
   * @property {number} value - The updated state of the floorArea.
   */
  "floorArea",
],
  setup(__props, { emit }) {

const props = __props;





const editableWidth = computed({
  get: () => props.width,
  set: (value: Number) => emit("update:width", value),
});

const editableLength = computed({
  get: () => props.length,
  set: (value: Number) => emit("update:length", value),
});

const editableLengthSuffix = computed({
  get: () => props.width,
  set: (value: Number) => emit("update:lengthSuffix", value),
});

const editableWidthSuffix = computed({
  get: () => props.length,
  set: (value: Number) => emit("update:widthSuffix", value),
});

const measurement1Main = ref(0);
const measurement1Minor = ref(0);
const measurement2Main = ref(0);
const measurement2Minor = ref(0);

const measurementMainLabel = computed(() => {
  return props.measurementType == MetricImperial.Imperial ? `ft` : `m`;
});

const measurementMinorLabel = computed(() => {
  return props.measurementType == MetricImperial.Imperial ? `inches` : `cm`;
});

const measurement1Alternative = computed(() => {
  if (!editableWidth.value && editableWidth.value != 0) {
    return "";
  }

  return props.measurementType == MetricImperial.Imperial
    ? `${editableWidth.value} (m)`
    : `${convertMetresToFeetAndInchesString(editableWidth.value)} (ft)`;
});

const measurement2Alternative = computed(() => {
  if (!editableLength.value && editableLength.value != 0) {
    return "";
  }

  return props.measurementType == MetricImperial.Imperial
    ? `${editableLength.value} (m)`
    : `${convertMetresToFeetAndInchesString(editableLength.value)} (ft)`;
});

// Users cant enter more than 99 cms or 11 inches, they have to enter a foot or meter instead of
// or as well as this number, or a smaller number.
const minorMeasurementMaxValue = computed(() => {
  return props.measurementType == MetricImperial.Imperial ? 11 : 99;
});

const measurementUpdated = () => {
  if (props.measurementType == MetricImperial.Imperial) {
    editableWidth.value = convertFeetToMetres(
      measurement1Main.value,
      measurement1Minor.value,
    );
    editableLength.value = convertFeetToMetres(
      measurement2Main.value,
      measurement2Minor.value,
    );
  } else {
    editableWidth.value = convertMetresAndCentimetresToMetres(
      measurement1Main.value,
      measurement1Minor.value,
    );
    editableLength.value = convertMetresAndCentimetresToMetres(
      measurement2Main.value,
      measurement2Minor.value,
    );
  }

  if (editableWidth.value && editableLength.value) {
    emit("floorArea");
  }
};

watch(
  () => props.formatData,
  () => {
    setMeasurementValues();
  },
);

onMounted(() => {
  setMeasurementValues();
});

const setMeasurementValues = () => {
  // We need to ensure these are reset each time the modal is opened so that a previous room's measurement don't appear.
  // This usually doesn't happen without these 4 lines but it does happen sporadically.
  measurement1Main.value = 0;
  measurement1Minor.value = 0;
  measurement2Main.value = 0;
  measurement2Minor.value = 0;

  formatWidth();
  formatLength();
};

const formatWidth = () => {
  if (props.measurementType == MetricImperial.Imperial) {
    const widthImperial = convertMetresToFeetAndInches(editableWidth.value);
    measurement1Main.value = widthImperial.feet;
    measurement1Minor.value = widthImperial.inches;
  } else {
    const widthMetric = convertMetresToMetresAndCentimetres(
      editableWidth.value,
    );
    measurement1Main.value = widthMetric.metres;
    measurement1Minor.value = widthMetric.centimetres;
  }
};

const formatLength = () => {
  if (props.measurementType == MetricImperial.Imperial) {
    const lengthImperial = convertMetresToFeetAndInches(editableLength.value);
    measurement2Main.value = lengthImperial.feet;
    measurement2Minor.value = lengthImperial.inches;
  } else {
    const lengthMetric = convertMetresToMetresAndCentimetres(
      editableLength.value,
    );
    measurement2Main.value = lengthMetric.metres;
    measurement2Minor.value = lengthMetric.centimetres;
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", null, [
      _createElementVNode("div", null, _toDisplayString(props.firstLabel), 1),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(NumberBoxField, {
          label: measurementMainLabel.value,
          modelValue: measurement1Main.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((measurement1Main).value = $event)),
          width: _unref(FieldSize).Small,
          onValueChanged: _cache[1] || (_cache[1] = ($event: any) => (measurementUpdated())),
          format: "#"
        }, null, 8, ["label", "modelValue", "width"]),
        _createVNode(NumberBoxField, {
          label: measurementMinorLabel.value,
          modelValue: measurement1Minor.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((measurement1Minor).value = $event)),
          width: _unref(FieldSize).Small,
          onValueChanged: _cache[3] || (_cache[3] = ($event: any) => (measurementUpdated())),
          maxValue: minorMeasurementMaxValue.value,
          format: "#"
        }, null, 8, ["label", "modelValue", "width", "maxValue"]),
        _createElementVNode("div", _hoisted_2, [
          _hoisted_3,
          _createVNode(AcquaintInfoBox, {
            text: measurement1Alternative.value,
            width: _unref(FieldSize).Medium
          }, null, 8, ["text", "width"])
        ]),
        (__props.displaySuffix)
          ? (_openBlock(), _createBlock(SystemCodeSingleSelect, {
              key: 0,
              codeType: _unref(CodeType).RoomMeasurementSuffix,
              label: "Suffix",
              modelValue: editableWidthSuffix.value,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((editableWidthSuffix).value = $event)),
              width: _unref(FieldSize).Medium
            }, null, 8, ["codeType", "modelValue", "width"]))
          : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("div", null, [
      _createElementVNode("div", null, _toDisplayString(props.secondLabel), 1),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(NumberBoxField, {
          label: measurementMainLabel.value,
          modelValue: measurement2Main.value,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((measurement2Main).value = $event)),
          width: _unref(FieldSize).Small,
          onValueChanged: _cache[6] || (_cache[6] = ($event: any) => (measurementUpdated())),
          format: "#"
        }, null, 8, ["label", "modelValue", "width"]),
        _createVNode(NumberBoxField, {
          label: measurementMinorLabel.value,
          modelValue: measurement2Minor.value,
          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((measurement2Minor).value = $event)),
          width: _unref(FieldSize).Small,
          onValueChanged: _cache[8] || (_cache[8] = ($event: any) => (measurementUpdated())),
          maxValue: minorMeasurementMaxValue.value,
          format: "#"
        }, null, 8, ["label", "modelValue", "width", "maxValue"]),
        _createElementVNode("div", _hoisted_5, [
          _hoisted_6,
          _createVNode(AcquaintInfoBox, {
            text: measurement2Alternative.value,
            width: _unref(FieldSize).Medium
          }, null, 8, ["text", "width"])
        ]),
        (__props.displaySuffix)
          ? (_openBlock(), _createBlock(SystemCodeSingleSelect, {
              key: 0,
              codeType: _unref(CodeType).RoomMeasurementSuffix,
              label: "Suffix",
              modelValue: editableLengthSuffix.value,
              "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((editableLengthSuffix).value = $event)),
              width: _unref(FieldSize).Medium
            }, null, 8, ["codeType", "modelValue", "width"]))
          : _createCommentVNode("", true)
      ])
    ])
  ], 64))
}
}

})